import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { CAREER_PATH_STAGES } from '@/modules/Employee/constants'
import {
    createCareerPath,
    createCareerPathNote,
    extendCareerPath,
    getCareerPaths,
    transitionCareerPath,
    updateCareerPath,
} from '@/modules/Employee/services/career-path'
import type {
    CareerPath,
    CareerPathPayload,
    CareerPathTransitionAction,
} from '@/modules/Employee/types/career-path'
import dayjs from 'dayjs'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Loading } from 'vnetwork-ui'
import type { TransitionAction } from './career-path-types'

export function useCareerPath(employeeId?: number) {
    const params = useParams()
    const id = employeeId || Number(params.id)
    const { t } = useTranslation('employee')

    // UI state
    const [expandedPaths, setExpandedPaths] = useState<Set<number>>(new Set())
    const [showStageDialog, setShowStageDialog] = useState(false)
    const [editingStage, setEditingStage] = useState<CareerPath | null>(null)
    const [noteDialogPathId, setNoteDialogPathId] = useState<number | null>(
        null
    )
    const [transitionDialog, setTransitionDialog] = useState<{
        action: TransitionAction
        currentPath: CareerPath
    } | null>(null)
    const [extendDialog, setExtendDialog] = useState<CareerPath | null>(null)

    // Fetch career paths
    const { data, renderState, refetch } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.EMPLOYEE_CAREER_PATHS, id],
        queryFn: () => getCareerPaths(id),
        enabled: !!id,
        loadingComponent: React.createElement(
            'div',
            { className: 'flex justify-center items-center min-h-[200px]' },
            React.createElement(Loading.CircularProgress, {
                width: 40,
                height: 40,
            })
        ),
    })

    // Mutations
    const { mutateAsync: createMutation, isPending: isCreating } =
        useMutationWithHandler(
            { mutationFn: createCareerPath },
            {
                reactQueryKeys: QUERY_KEYS.EMPLOYEE_CAREER_PATHS,
                successMessage: t('create-success'),
                onSuccess: () => {
                    setShowStageDialog(false)
                    refetch()
                },
            }
        )

    const { mutateAsync: updateMutation, isPending: isUpdating } =
        useMutationWithHandler(
            { mutationFn: updateCareerPath },
            {
                reactQueryKeys: QUERY_KEYS.EMPLOYEE_CAREER_PATHS,
                successMessage: t('update-success'),
                onSuccess: () => {
                    setEditingStage(null)
                    setTransitionDialog(null)
                    refetch()
                },
            }
        )

    const { mutateAsync: createNoteMutation, isPending: isCreatingNote } =
        useMutationWithHandler(
            { mutationFn: createCareerPathNote },
            {
                reactQueryKeys: QUERY_KEYS.EMPLOYEE_CAREER_PATHS,
                successMessage: t('create-success'),
                onSuccess: () => {
                    setNoteDialogPathId(null)
                    refetch()
                },
            }
        )

    const { mutateAsync: transitionMutation, isPending: isTransitioning } =
        useMutationWithHandler(
            { mutationFn: transitionCareerPath },
            {
                reactQueryKeys: QUERY_KEYS.EMPLOYEE_CAREER_PATHS,
                onSuccess: () => {
                    setTransitionDialog(null)
                    refetch()
                },
            }
        )

    const { mutateAsync: extendMutation, isPending: isExtending } =
        useMutationWithHandler(
            { mutationFn: extendCareerPath },
            {
                reactQueryKeys: QUERY_KEYS.EMPLOYEE_CAREER_PATHS,
                successMessage: t('update-success'),
                onSuccess: () => {
                    setExtendDialog(null)
                    refetch()
                },
            }
        )

    // Computed
    const careerPaths = data?.results || []
    const sortedPaths = [...careerPaths].sort(
        (a, b) =>
            new Date(b.career_path_start_date).getTime() -
            new Date(a.career_path_start_date).getTime()
    )
    const stages = CAREER_PATH_STAGES(t)
    const hasStages = sortedPaths.length > 0
    const lastPath = hasStages ? sortedPaths[0] : null
    const latestStage = useMemo(() => {
        if (!lastPath) return 'intern'
        return lastPath.career_path_stage
    }, [lastPath])
    // Stages the employee actually went through (for progression bar)
    const passedStages = useMemo(
        () => new Set(careerPaths.map((p) => p.career_path_stage)),
        [careerPaths]
    )

    // Toggle notes expand
    const toggleExpand = (pathId: number) => {
        setExpandedPaths((prev) => {
            const next = new Set(prev)
            next.has(pathId) ? next.delete(pathId) : next.add(pathId)
            return next
        })
    }

    // Handlers
    const handleCreateOrUpdateStage = (values: any) => {
        const payload: CareerPathPayload = {
            career_path_stage: values.career_path_stage,
            career_path_start_date: dayjs(values.career_path_start_date).format(
                'YYYY-MM-DD'
            ),
            career_path_end_date: values.career_path_end_date
                ? dayjs(values.career_path_end_date).format('YYYY-MM-DD')
                : undefined,
            career_path_note: values.career_path_note || undefined,
        }
        if (editingStage) {
            updateMutation({
                employeeId: id,
                careerPathId: editingStage.career_path_id,
                data: payload,
            })
        } else {
            createMutation({ employeeId: id, data: payload })
        }
    }

    const handleTransition = async (values: {
        transition_date: any
        career_path_note?: string
    }) => {
        if (!transitionDialog) return
        const { action, currentPath } = transitionDialog
        await transitionMutation({
            employeeId: id,
            careerPathId: currentPath.career_path_id,
            data: {
                action: action.key as CareerPathTransitionAction,
                date: dayjs(values.transition_date).format('YYYY-MM-DD'),
                note: values.career_path_note || undefined,
            },
        })
    }

    const handleCreateNote = (values: { note_content: string }) => {
        if (!noteDialogPathId) return
        createNoteMutation({
            employeeId: id,
            careerPathId: noteDialogPathId,
            data: { note_content: values.note_content },
        })
    }

    const handleExtendPeriod = async (values: {
        new_end_date: any
        extend_reason: string
    }) => {
        if (!extendDialog) return
        await extendMutation({
            employeeId: id,
            careerPathId: extendDialog.career_path_id,
            data: {
                new_end_date: dayjs(values.new_end_date).format('YYYY-MM-DD'),
                reason: values.extend_reason || undefined,
            },
        })
    }

    return {
        // Data
        id,
        sortedPaths,
        latestStage,
        passedStages,
        stages,
        hasStages,
        renderState,
        t,

        // UI state
        expandedPaths,
        showStageDialog,
        editingStage,
        noteDialogPathId,
        transitionDialog,
        extendDialog,

        // State setters
        setShowStageDialog,
        setEditingStage,
        setNoteDialogPathId,
        setTransitionDialog,
        setExtendDialog,
        toggleExpand,

        // Handlers
        handleCreateOrUpdateStage,
        handleTransition,
        handleCreateNote,
        handleExtendPeriod,

        // Loading states
        isCreating,
        isUpdating,
        isTransitioning,
        isCreatingNote,
        isExtending,
    }
}
